import { RightSquareOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { Button, Empty, Input } from "antd";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useGetGroups2, useGetSubscriptionLimits } from "../../api/api.get";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { capitalize } from "../../utils/functions";
import { useCreateGroup } from "../../api/api";

export default function Groups() {
  const { mutateAsync } = useCreateGroup();
  const { data: groupsData, refetch: refetchGroups } = useGetGroups2();
  const { notification, notifyError, notifySuccess } = useNotifications();
  const { data: subscriptionLimitData } = useGetSubscriptionLimits();

  const formik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        name: values.name,
      })
        .then(async () => {
          await refetchGroups();
          notifySuccess({ description: "Account successfully created" });
          formik.resetForm();
        })
        .catch(() => notifyError("Whoops! Please try again."));
    },
  });

  const navigate = useNavigate();

  return (
    <div className="min-h-screen mx-auto max-w-7xl bg-slate-50">
      {notification}

      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Groups</h1>
            <p className="text-muted-foreground text-gray-500 text-sm">
              Create multiple groups to consolidate and manage your expenses
              separately.
            </p>
          </div>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Accounts List */}

            <div className="lg:col-span-2">
              {!groupsData ||
                (groupsData && groupsData.length === 0 && (
                  <Empty description="No Groups Found" className="mt-10" />
                ))}

              {groupsData && groupsData.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Your Groups
                    </CardTitle>
                    <CardDescription>
                      {groupsData?.length} Group
                      {groupsData?.length !== 1 ? "s" : ""}{" "}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {groupsData.map((group) => {
                        return (
                          <div
                            key={group.id}
                            className="p-4 border border-slate-200 rounded-lg hover:shadow-md hover:border-primary/50 transition cursor-pointer bg-slate-50 hover:bg-slate-100"
                          >
                            <div
                              onClick={() => navigate(`/groups/${group.id}`)}
                              className="flex justify-between items-start mb-3"
                            >
                              <div className="flex flex-row items-center justify-center gap-4">
                                <UsergroupAddOutlined />
                                <h3 className="font-semibold text-foreground text-lg">
                                  {capitalize(group.name)}
                                </h3>
                              </div>
                              <div className="flex gap-2">
                                <RightSquareOutlined className="text-2xl" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Panel: Add Account Form */}
            <div className="flex flex-col gap-8">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Add Group</CardTitle>
                  <CardDescription className="text-gray-500">
                    For eg. "My Family" or "With Parents"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Group Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., Summer Vacation"
                        {...formik.getFieldProps("name")}
                        required
                      />
                      {!!formik.errors.name && (
                        <p className="text-red-500 text-xs">
                          {formik.errors.name}
                        </p>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-between gap-2 pt-4">
                      <Button
                        className="flex-1"
                        onClick={formik.submitForm}
                        type="primary"
                        size="large"
                        disabled={Object.keys(formik.errors).length > 0}
                      >
                        Add Group
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={() => formik.resetForm()}
                        type="text"
                        size="large"
                      >
                        Clear
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
              <p className="gap-2 px-6 py-4 border-amber-300 border bg-orange-100 rounded-xl text-xs">
                Your subscription allows you to add upto{" "}
                <strong>{subscriptionLimitData?.groups ?? 1} group(s)</strong> .
                To add more, please{" "}
                <Link to={"/subscriptions"} className="underline text-blue-700">
                  upgrade
                </Link>{" "}
                your subscription or wait until the 1st of next month.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
